import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, ChevronLeft, Pencil } from "lucide-react";
import { EDIT_ICON, PHOTO } from "../../../assets";

function Settings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"personal" | "password">(
    "personal",
  );

  // Personal Info State
  const [personalInfo, setPersonalInfo] = useState({
    name: "Ademola Samuel",
    email: "G&G@gmail.com",
    scn: "839238CN",
    lawFirm: "AdemolaSamuel & Co",
    address: "12 Adeniyi street",
    phone: "+234 087654225678",
    whatsapp: "+234 087654225678",
    picture: null as File | null,
  });

  // Password State
  const [passwordInfo, setPasswordInfo] = useState({
    currentPassword: "****************",
    newPassword: "****************",
    confirmPassword: "****************",
  });

  const handlePersonalChange = (field: string, value: string) => {
    setPersonalInfo({ ...personalInfo, [field]: value });
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordInfo({ ...passwordInfo, [field]: value });
  };

  return (
    <div className="flex flex-col h-full min-h-screen bg-white pb-20">
      {/* Header Section */}
      <div className="px-8 py-6 border-b border-[#E9EAEB]">
        <div className="flex items-center gap-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-[#1A1A1A]" />
          </button>
          <div className="flex items-center gap-x-4">
            <img
              src={PHOTO}
              alt="Ademola Samuel"
              className="w-12 h-12 rounded-full object-cover"
            />
            <div className="flex flex-col">
              <h1 className="text-xl font-bold text-[#1A1A1A]">
                Ademola Samuel
              </h1>
              <span className="text-sm text-[#475467]">Police Officer</span>
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="px-8 py-4 flex items-center gap-x-2 text-sm border-b border-[#E9EAEB]">
        <Link
          to="/police/overview"
          className="text-[#98A2B3] hover:text-[#1A1A1A] transition-colors"
        >
          Home
        </Link>
        <ChevronRight className="w-4 h-4 text-[#98A2B3]" />
        <span className="font-medium text-[#1A1A1A]">Ademola Samuel</span>
      </div>

      {/* Tabs */}
      <div className="px-8 mt-6">
        <div className="flex items-center gap-x-8 border-b border-[#E9EAEB]">
          <button
            onClick={() => setActiveTab("personal")}
            className={`pb-3 text-sm font-semibold transition-colors relative ${
              activeTab === "personal"
                ? "text-[#1A1A1A] border-b-2 border-[#1A1A1A]"
                : "text-[#98A2B3] hover:text-[#1A1A1A]"
            }`}
          >
            Personal Information
          </button>
          <button
            onClick={() => setActiveTab("password")}
            className={`pb-3 text-sm font-semibold transition-colors relative ${
              activeTab === "password"
                ? "text-[#1A1A1A] border-b-2 border-[#1A1A1A]"
                : "text-[#98A2B3] hover:text-[#1A1A1A]"
            }`}
          >
            Password
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-8 py-8 bg-white">
        <div className="max-w-4xl border border-[#E9EAEB] rounded-xl overflow-hidden">
          {/* Section Header */}
          <div className="bg-[#F9FAFB] px-6 py-4 border-b border-[#E9EAEB]">
            <h2 className="text-lg font-semibold text-[#1A1A1A]">
              {activeTab === "personal" ? "Personal Information" : "Password"}
            </h2>
          </div>

          <div className="p-8">
            {activeTab === "personal" ? (
              <div className="flex flex-col gap-y-8">
                <div className="grid grid-cols-2 gap-x-8 gap-y-8">
                  {/* Name */}
                  <div className="flex flex-col gap-y-2">
                    <label className="text-sm font-medium text-[#98A2B3]">
                      Name
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={personalInfo.name}
                        onChange={(e) =>
                          handlePersonalChange("name", e.target.value)
                        }
                        className="w-full px-4 py-3 border border-[#E9EAEB] rounded-lg text-[#667085] focus:outline-none focus:border-[#00B074]"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex flex-col gap-y-2">
                    <label className="text-sm font-medium text-[#98A2B3]">
                      Email address
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        value={personalInfo.email}
                        onChange={(e) =>
                          handlePersonalChange("email", e.target.value)
                        }
                        className="w-full px-4 py-3 border border-[#E9EAEB] rounded-lg text-[#667085] focus:outline-none focus:border-[#00B074]"
                      />
                      <img
                        src={EDIT_ICON}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D0D5DD] cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* SCN */}
                  <div className="flex flex-col gap-y-2">
                    <label className="text-sm font-medium text-[#98A2B3]">
                      SCN (Supreme Court Number)
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={personalInfo.scn}
                        onChange={(e) =>
                          handlePersonalChange("scn", e.target.value)
                        }
                        className="w-full px-4 py-3 border border-[#E9EAEB] rounded-lg text-[#667085] focus:outline-none focus:border-[#00B074]"
                      />
                    </div>
                  </div>

                  {/* Law Firm */}
                  <div className="flex flex-col gap-y-2">
                    <label className="text-sm font-medium text-[#98A2B3]">
                      Law firm
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={personalInfo.lawFirm}
                        onChange={(e) =>
                          handlePersonalChange("lawFirm", e.target.value)
                        }
                        className="w-full px-4 py-3 border border-[#E9EAEB] rounded-lg text-[#667085] focus:outline-none focus:border-[#00B074]"
                      />
                      <img
                        src={EDIT_ICON}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D0D5DD] cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex flex-col gap-y-2">
                    <label className="text-sm font-medium text-[#98A2B3]">
                      Address for service
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={personalInfo.address}
                        onChange={(e) =>
                          handlePersonalChange("address", e.target.value)
                        }
                        className="w-full px-4 py-3 border border-[#E9EAEB] rounded-lg text-[#667085] focus:outline-none focus:border-[#00B074]"
                      />
                      <img
                        src={EDIT_ICON}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D0D5DD] cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex flex-col gap-y-2">
                    <label className="text-sm font-medium text-[#98A2B3]">
                      Telephone number
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={personalInfo.phone}
                        onChange={(e) =>
                          handlePersonalChange("phone", e.target.value)
                        }
                        className="w-full px-4 py-3 border border-[#E9EAEB] rounded-lg text-[#667085] focus:outline-none focus:border-[#00B074]"
                      />
                      <img
                        src={EDIT_ICON}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D0D5DD] cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* Whatsapp */}
                  <div className="flex flex-col gap-y-2">
                    <label className="text-sm font-medium text-[#98A2B3]">
                      Whatsapp number
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={personalInfo.whatsapp}
                        onChange={(e) =>
                          handlePersonalChange("whatsapp", e.target.value)
                        }
                        className="w-full px-4 py-3 border border-[#E9EAEB] rounded-lg text-[#667085] focus:outline-none focus:border-[#00B074]"
                      />
                      <img
                        src={EDIT_ICON}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D0D5DD] cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* Upload Picture */}
                  <div className="flex flex-col gap-y-2">
                    <label className="text-sm font-medium text-[#98A2B3]">
                      Upload your picture
                    </label>
                    <div className="relative w-full px-4 py-2 border border-[#E9EAEB] rounded-lg border-dashed flex items-center justify-between">
                      <span className="text-[#667085]">
                        Select to upload picture
                      </span>
                      <label className="cursor-pointer bg-[#BDE0CE] text-[#1A1A1A] text-sm font-medium px-4 py-2 rounded-md hover:bg-[#a6d1bb] transition-colors">
                        Upload
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-4">
                  <button className="bg-[#00B074] text-white px-8 py-3 rounded-lg font-medium hover:bg-[#009b65] transition-colors">
                    Update
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-y-8">
                {/* Current Password */}
                <div className="flex flex-col gap-y-2">
                  <label className="text-sm font-medium text-[#98A2B3]">
                    Current password
                  </label>
                  <input
                    type="password"
                    value={passwordInfo.currentPassword}
                    onChange={(e) =>
                      handlePasswordChange("currentPassword", e.target.value)
                    }
                    className="w-full px-4 py-3 border border-[#E9EAEB] rounded-lg text-[#667085] focus:outline-none focus:border-[#00B074]"
                  />
                </div>

                {/* New Password */}
                <div className="flex flex-col gap-y-2">
                  <label className="text-sm font-medium text-[#98A2B3]">
                    New password
                  </label>
                  <input
                    type="password"
                    value={passwordInfo.newPassword}
                    onChange={(e) =>
                      handlePasswordChange("newPassword", e.target.value)
                    }
                    className="w-full px-4 py-3 border border-[#E9EAEB] rounded-lg text-[#667085] focus:outline-none focus:border-[#00B074]"
                  />
                </div>

                {/* Confirm Password */}
                <div className="flex flex-col gap-y-2">
                  <label className="text-sm font-medium text-[#98A2B3]">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    value={passwordInfo.confirmPassword}
                    onChange={(e) =>
                      handlePasswordChange("confirmPassword", e.target.value)
                    }
                    className="w-full px-4 py-3 border border-[#E9EAEB] rounded-lg text-[#667085] focus:outline-none focus:border-[#00B074]"
                  />
                </div>

                <div className="flex items-center justify-end gap-x-4 mt-8">
                  <button className="px-8 py-3 border border-[#D0D5DD] rounded-lg text-[#344054] font-medium hover:bg-gray-50 transition-colors">
                    Update
                  </button>
                  <button className="bg-[#00B074] text-white px-8 py-3 rounded-lg font-medium hover:bg-[#009b65] transition-colors">
                    Update password
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
